import { stegaClean } from "next-sanity";

import type { MarketingPopupFragmentResult } from "@/lib/sanity/sanity.types";

type Global = MarketingPopupFragmentResult["global"];
type Overrides = MarketingPopupFragmentResult["overrides"];

export type ResolvedPopup =
  | NonNullable<Global>
  | NonNullable<Overrides[number]["popup"]>;

/** `/stay/` and `/stay` are the same page; `/` stays `/`. */
function normalisePath(path: string | null | undefined): string | null {
  const clean = stegaClean(path);
  if (!clean) return null;
  const trimmed = clean.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function parseDate(value: string | null | undefined): number | null {
  const clean = stegaClean(value);
  if (!clean) return null;
  const time = new Date(clean).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * An empty start or end means "no bound that side", so a campaign with neither
 * runs until the toggle is switched off — which is why the toggle stays: a
 * campaign can run open-ended and be stopped by hand, with no dates involved.
 */
export function isWithinWindow(popup: ResolvedPopup, now: Date): boolean {
  const at = now.getTime();
  const start = parseDate(popup.startDate);
  const end = parseDate(popup.endDate);

  if (start !== null && at < start) return false;
  if (end !== null && at > end) return false;
  return true;
}

/** Live means switched on and inside its schedule. */
function isLive(
  popup: ResolvedPopup | null | undefined,
  now: Date,
): popup is ResolvedPopup {
  if (!popup) return false;
  return popup.enabled === true && isWithinWindow(popup, now);
}

/**
 * Picks the pop-up for the current page and moment.
 *
 * A page carrying a Pop-Up Override block shows that campaign; every other page
 * shows the global one set in Website Settings. Nothing is inferred from how
 * many campaigns exist or when they were last edited — the global pop-up is
 * whatever Settings points at, and empty means no pop-up.
 *
 * An override that is switched off or outside its schedule falls back to the
 * global campaign rather than to nothing, so a spent announcement quietly gives
 * way to the standing message.
 */
export function resolveMarketingPopup({
  global,
  overrides,
  pathname,
  locale,
  now,
}: {
  global: Global;
  overrides: Overrides;
  pathname: string;
  locale: string;
  now: Date;
}): ResolvedPopup | null {
  const currentPath = normalisePath(pathname);

  const match = overrides?.find((entry) => {
    const paths = entry.paths as Record<string, string | null> | null;
    const localePath = normalisePath(paths?.[locale]);
    return localePath !== null && localePath === currentPath;
  });

  if (isLive(match?.popup, now)) {
    return match.popup;
  }

  return isLive(global, now) ? global : null;
}
